import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { addHolding, Lot } from '../../services/holdingsService';

interface AddPositionModalProps {
  uid: string;
  onClose: () => void;
  onAdded: () => void;
}

export function AddPositionModal({ uid, onClose, onAdded }: AddPositionModalProps) {
  const [ticker, setTicker] = useState('');
  const [shares, setShares] = useState('');
  const [totalCostPaid, setTotalCostPaid] = useState('');
  const [purchaseDate, setPurchaseDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticker.trim()) {
      setError('Ticker symbol is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formattedTicker = ticker.toUpperCase().replace(/\./g, '-');
      
      // Fetch company name from FMP
      let companyName = formattedTicker;
      try {
        const res = await fetch(`/api/fmp/stable/profile?symbol=${encodeURIComponent(formattedTicker)}`);
        if (res.ok) {
          const data = await res.json();
          if (data && data.length > 0 && data[0].companyName) {
            companyName = data[0].companyName;
          }
        }
      } catch (err) {
        console.warn('Failed to fetch company profile, using ticker as name');
      }

      const sharesNum = shares ? parseFloat(shares) : 0;
      const totalCostNum = totalCostPaid ? parseFloat(totalCostPaid) : 0;
      const costPerShare = sharesNum > 0 && totalCostNum > 0
        ? totalCostNum / sharesNum
        : 0;

      const lot: Lot = {
        id: crypto.randomUUID(),
        type: 'buy',
        shares: sharesNum,
        totalCostPaid: totalCostNum,
        costPerShare,
        datePurchased: new Date(purchaseDate).toISOString(),
        source: 'manual',
      };

      await addHolding(uid, {
        ticker: formattedTicker,
        companyName,
        lots: sharesNum > 0 || totalCostNum > 0 ? [lot] : [],
        dateAdded: new Date().toISOString(),
        notes,
        source: 'manual',
      });

      onAdded();
      onClose();
    } catch (err) {
      console.error('Add position failed:', err);
      setError('Failed to add position. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="oi-overlay w-full max-w-md relative z-10 text-left">
        <button 
          onClick={onClose}
          className="absolute right-4 top-4 text-ink-500 hover:text-oxblood transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 border-b border-ink-200 text-left">
          <h2 className="text-xl font-serif font-medium italic tracking-tight">Add Position</h2>
          <p className="text-sm text-ink-500 mt-1">Track your holdings for Keeper evaluation.</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-danger-50 text-danger-700 text-sm rounded-md border border-danger-100">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-ink-600 uppercase tracking-wider mb-1.5">Ticker Symbol *</label>
            <input
              type="text"
              required
              value={ticker}
              onChange={(e) => setTicker(e.target.value.toUpperCase())}
              placeholder="e.g. AAPL"
              className="w-full px-3 py-2 bg-surface-2 border border-ink-200 rounded-md focus:outline-none focus:ring-1 focus:ring-oxblood focus:border-oxblood font-mono"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-ink-600 uppercase tracking-wider mb-1.5">Shares</label>
              <input
                type="number"
                step="any"
                min="0"
                value={shares}
                onChange={(e) => setShares(e.target.value)}
                placeholder="Optional"
                className="w-full px-3 py-2 bg-surface-2 border border-ink-200 rounded-md focus:outline-none focus:ring-1 focus:ring-oxblood focus:border-oxblood"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-600 uppercase tracking-wider mb-1.5">Total Cost Paid ($)</label>
              <input
                type="number"
                step="any"
                min="0"
                value={totalCostPaid}
                onChange={(e) => setTotalCostPaid(e.target.value)}
                placeholder="e.g. 5000"
                className="w-full px-3 py-2 bg-surface-2 border border-ink-200 rounded-md focus:outline-none focus:ring-1 focus:ring-oxblood focus:border-oxblood"
              />
              <p className="text-[10px] text-ink-500 mt-1">Enter total amount paid for all shares. We'll calculate per-share cost.</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-ink-600 uppercase tracking-wider mb-1.5">
              Purchase Date
            </label>
            <input
              type="date"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 bg-surface-2 border border-ink-200 rounded-md focus:outline-none focus:ring-1 focus:ring-oxblood focus:border-oxblood"
            />
            <p className="text-[10px] text-ink-500 mt-1">
              When did you buy these shares? Used for performance chart accuracy.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-ink-600 uppercase tracking-wider mb-1.5">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional personal notes..."
              rows={3}
              className="w-full px-3 py-2 bg-surface-2 border border-ink-200 rounded-md focus:outline-none focus:ring-1 focus:ring-oxblood focus:border-oxblood resize-none"
            />
          </div>

          <div className="pt-2 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-ink-600 hover:text-ink hover:bg-surface-2 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-paper bg-oxblood hover:bg-oxblood-800 rounded-md transition-colors flex items-center justify-center gap-2 min-w-[100px]"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Position"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
